import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { useHistory } from "react-router-dom";

import useAccountMetadata from "@/hooks/useAccountMetadata";
import { isAccountInHIPAAMode, isAccountInHIPAAModeV2 } from "@/util";
import { destinationHasPropertyMetadata } from "@/destinations/DestinationPropertyMetadata";
import { Button } from "@/design-system/components/Button/Button";
import { ConditionalTooltip } from "@/design-system/components/Tooltip/Tooltip";

interface DestinationConfigRowProps {
  name: string;
  textColor?: string;
  labelText?: string;
  labelStyles?: React.CSSProperties;
  onOpenConfigModal?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  buttonText?: string;
  buttonComponent?: React.ReactNode;
  /** Inset custom controls (e.g. switches) from the row edge to align with Configure buttons. */
  buttonInsetClassName?: string;
  className?: string;
  leadingClassName?: string;
  alignTop?: boolean;
}

export function DestinationConfigRow({
  name,
  textColor,
  labelText,
  labelStyles = {},
  onOpenConfigModal,
  disabled = false,
  disabledReason = "Requires API credentials",
  buttonText = "Configure",
  className,
  buttonComponent = null,
  buttonInsetClassName,
  leadingClassName,
  alignTop = false,
}: DestinationConfigRowProps) {
  return (
    <div
      data-testid="destination-config-row"
      role="group"
      aria-label={name}
      className={clsx(
        "flex justify-between p-4 first:rounded-t-md last:rounded-b-md",
        alignTop ? "items-start" : "items-center",
        disabled ? "bg-surface-sunken" : "",
        className && className,
      )}
    >
      <div className={leadingClassName}>
        <h5 className="text-sm font-medium">{name}</h5>
        {!disabled && labelText && (
          <p
            className="text-textSubtle text-sm font-normal"
            style={{ color: textColor, ...labelStyles }}
          >
            {labelText}
          </p>
        )}
      </div>
      <div className={clsx("text-right", alignTop && "pt-0.5")}>
        {buttonComponent ? (
          <div className={clsx("inline-flex", buttonInsetClassName)}>
            {buttonComponent}
          </div>
        ) : (
          <ConditionalTooltip
            condition={disabled}
            content={disabledReason}
            contentProps={{ side: "left" }}
          >
            <Button
              onClick={disabled ? undefined : onOpenConfigModal}
              variant="outline"
              size="sm"
              disabled={disabled}
            >
              {buttonText}
            </Button>
          </ConditionalTooltip>
        )}
      </div>
    </div>
  );
}

export function DestinationHIPAARow({
  onOpenConfigModal,
  disabled = false,
  configuredDestination,
}) {
  const accountMetadata = useAccountMetadata();

  if (!isAccountInHIPAAMode(accountMetadata)) {
    return null;
  }
  let labelConfigurationText = configuredDestination.has_baa
    ? "Allowing PHI"
    : "Restricting PHI";

  return (
    <DestinationConfigRow
      name="HIPAA settings"
      labelText={labelConfigurationText}
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Configure"
      disabled={disabled}
    />
  );
}

DestinationHIPAARow.propTypes = {
  onOpenConfigModal: PropTypes.func.isRequired,
  configuredDestination: PropTypes.object.isRequired,
};

export function DestinationPHIAllowlistConfigRow({
  destination,
  configuredDestination,
  disabled,
}) {
  const accountMetadata = useAccountMetadata();
  const history = useHistory();

  const onConfigure = () => {
    history.push(
      destination.configPageUrl() + "/phiallowlist/" + configuredDestination.id,
    );
  };

  if (!isAccountInHIPAAModeV2(accountMetadata)) {
    return null;
  }

  if (!destination.supportsServerSide) {
    return null;
  }

  let rowLabel = "HIPAA allow list";
  if (destinationHasPropertyMetadata(destination.key)) {
    rowLabel = "Advanced: " + rowLabel;
  }

  return (
    <DestinationConfigRow
      name={rowLabel}
      onOpenConfigModal={onConfigure}
      buttonText="Configure"
      disabled={disabled || configuredDestination.has_baa}
      disabledReason={
        configuredDestination.has_baa
          ? "Not configurable while a BAA is in place"
          : undefined
      }
    />
  );
}

DestinationPHIAllowlistConfigRow.propTypes = {
  destination: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};

export function DestinationConnectionModeRow({
  onOpenConfigModal,
  disabled = false,
  configuredDestination,
}) {
  let connectionModeLabel = "None specified";

  if (configuredDestination.connection_mode === "client") {
    connectionModeLabel = "Client-side";
  } else if (configuredDestination.connection_mode === "server") {
    connectionModeLabel = "Server-side";
  }
  return (
    <DestinationConfigRow
      name="Connection mode settings"
      labelText={connectionModeLabel}
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Configure"
      disabled={disabled || configuredDestination.has_baa}
      disabledReason={
        configuredDestination.has_baa
          ? "Not configurable while a BAA is in place"
          : undefined
      }
    />
  );
}

DestinationConnectionModeRow.propTypes = {
  onOpenConfigModal: PropTypes.func.isRequired,
  configuredDestination: PropTypes.object.isRequired,
};
